/**
 * ViewportCullingAdapter — model-space entity culling for paper-space viewports.
 *
 * Each paper-space VIEWPORT entity defines a rectangular window into model
 * space: the window is centred at `viewCenter`, has model-space height
 * `viewHeight`, and may be rotated by `viewTwistAngle` (radians).
 *
 * This module answers "which model-space entities are visible through this
 * viewport?" by:
 *   1. Computing the model-space AABB of the (possibly rotated) view window.
 *   2. Querying the RBush spatial index with that AABB.
 *   3. Filtering out entities whose layer name appears in the viewport's
 *      `frozenLayers` list.
 *
 * Results are cached per viewport id keyed on (viewCenter, viewHeight,
 * viewTwistAngle) so repeated queries in the same frame are O(1).  The
 * cache is invalidated via `invalidate(viewportId?)` whenever the camera,
 * VP parameters, or layer state change.
 *
 * AutoCAD parity notes:
 *   - Frozen layers are per-viewport (distinct from globally-off layers).
 *   - Twist (viewTwistAngle) rotates the VIEW around the Z axis; the
 *     model-space AABB must account for the rotation so off-screen entities
 *     are not culled when the view is tilted.
 *   - Viewport aspect ratio: viewWidth = viewHeight × (vpWidth / vpHeight).
 *     If vpHeight ≤ 0 we fall back to a 1:1 ratio to avoid division-by-zero.
 */

import {
  viewportWcsViewCenter,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
import type { SpatialIndex, ViewportRect } from "../types";
import { applyOffsetToBounds } from "../coordinateOrigin";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CacheEntry {
  visibleIds: Set<string>;
  viewCenterX: number;
  viewCenterY: number;
  // The DCS centre is only half the rect's anchor — the WCS view TARGET is
  // the other half (see `computeViewportModelBounds`). A target-only edit
  // (`viewTarget` moved, `viewCenter` untouched) MOVES the window, so it must
  // invalidate; without these two the adapter served the old visible set.
  viewTargetX: number;
  viewTargetY: number;
  viewHeight: number;
  viewTwistAngle: number;
  vpWidth: number;
  vpHeight: number;
  frozenLayersKey: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Compute the AABB (ViewportRect) in model space for the visible region of
 * a viewport.  Accounts for viewport rotation (twist) by expanding the rect
 * to its axis-aligned bounding box.
 *
 * The model-space width is derived from the viewport's paper-space aspect
 * ratio: `vpWidth / vpHeight × viewHeight`.  A degenerate viewport (zero
 * height) falls back to a 1:1 ratio so the query still returns some results.
 *
 * WINDOW ANCHOR: the rect is centred on the WCS point the window shows, which
 * is `target + R(twist)·viewCenter` (`drawing-model`'s `viewportWcsViewCenter`
 * — groups 12/22 are the centre in DCS, whose origin is the WCS view target
 * 17/27/37), NOT the raw 12/22 pair. This is the SAME reading
 * `rendering/viewportRenderPass.buildViewportModelCamera` positions the per-VP
 * camera with, and the two must not disagree: a cull rect anchored a whole
 * target-vector away from the camera would drop exactly the entities the
 * camera is pointed at. The twist appears TWICE and must: once INSIDE
 * `viewportWcsViewCenter` (`target + R(θ)·viewCenter`, the `ezdxf`-derived DCS
 * basis rotation) and once on the half-extent offsets rotated around that
 * centre below. That is not double-counting — it is the one rotation factored
 * over the sum, `target + R(θ)·(viewCenter + halfExtent)`, which is exactly
 * what `buildViewportModelCamera` composes from its position and its
 * `cam.up`.
 *
 * COORDINATE FRAME: the result is in the DOCUMENT's own model frame, because
 * every input it reads (`viewCenter`, `viewTarget`, `viewHeight`,
 * `viewTwistAngle`) is a raw DXF value off the `ViewportEntity`. Callers that
 * hand it to the spatial index must first translate it into the renderer's
 * offset-world frame — see `ViewportCullingAdapter.query` below.
 */
export function computeViewportModelBounds(viewport: ViewportEntity): ViewportRect {
  const { viewHeight, viewTwistAngle, width: vpWidth, height: vpHeight } =
    viewport;

  const aspectRatio = vpHeight > 0 ? vpWidth / vpHeight : 1;
  const halfH = viewHeight / 2;
  const halfW = halfH * aspectRatio;

  // Four half-extents corners of the un-rotated view window (relative to
  // viewCenter in model space):  TL, TR, BR, BL.
  const corners: [number, number][] = [
    [-halfW, halfH],
    [halfW, halfH],
    [halfW, -halfH],
    [-halfW, -halfH],
  ];

  // The WCS anchor of the window (see this function's doc) — `target +
  // R(twist)·viewCenter`; the half-extent rotation below is the SAME rotation,
  // factored over the sum.
  const wcsCenter = viewportWcsViewCenter(viewport);

  // Apply twist rotation (counter-clockwise in CAD convention).
  // viewTwistAngle stored in degrees (DXF group 51 raw) — convert to radians.
  const twistRad = (viewTwistAngle * Math.PI) / 180;
  const cos = Math.cos(twistRad);
  const sin = Math.sin(twistRad);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const [lx, ly] of corners) {
    const rx = lx * cos - ly * sin + wcsCenter.x;
    const ry = lx * sin + ly * cos + wcsCenter.y;
    if (rx < minX) minX = rx;
    if (rx > maxX) maxX = rx;
    if (ry < minY) minY = ry;
    if (ry > maxY) maxY = ry;
  }

  return { left: minX, top: minY, right: maxX, bottom: maxY };
}

// ---------------------------------------------------------------------------
// ViewportCullingAdapter
// ---------------------------------------------------------------------------

export class ViewportCullingAdapter {
  private readonly cache: Map<string, CacheEntry> = new Map();

  constructor(private readonly spatial: SpatialIndex) {}

  /**
   * Return the set of model-space entity ids visible through `viewport`.
   *
   * The result is cached until `invalidate(viewportId)` is called.  Cache
   * keys include all VP parameters that affect visibility:
   * viewCenter / viewHeight / viewTwistAngle / viewport dimensions /
   * frozenLayers list.
   */
  query(viewport: ViewportEntity): Set<string> {
    const frozenKey = viewport.frozenLayers.slice().sort().join("\0");
    const cached = this.cache.get(viewport.id);
    if (
      cached !== undefined &&
      cached.viewCenterX === viewport.viewCenter.x &&
      cached.viewCenterY === viewport.viewCenter.y &&
      cached.viewTargetX === (viewport.viewTarget?.x ?? 0) &&
      cached.viewTargetY === (viewport.viewTarget?.y ?? 0) &&
      cached.viewHeight === viewport.viewHeight &&
      cached.viewTwistAngle === viewport.viewTwistAngle &&
      cached.vpWidth === viewport.width &&
      cached.vpHeight === viewport.height &&
      cached.frozenLayersKey === frozenKey
    ) {
      return cached.visibleIds;
    }

    // `computeViewportModelBounds` reads the raw DXF `viewCenter` /
    // `viewHeight`, so its rect is in the DOCUMENT frame. The spatial index is
    // always in the renderer's OFFSET-world frame (see
    // `state/spatialBuilder.ts buildSpatialRecordsForSpaces`, "Coordinate
    // frame — the ONE seam"), so translate before querying. This was the one
    // query site that was document-frame and therefore accidentally agreed
    // with the old raw-world cache-direct index; under a non-zero locked
    // origin it disagreed with the legacy offset-world index instead, and a
    // paper layout's viewports culled everything. Origin 0 → identity.
    const modelBounds = computeViewportModelBounds(viewport);
    const offsetBounds = applyOffsetToBounds({
      minX: modelBounds.left,
      minY: modelBounds.top,
      maxX: modelBounds.right,
      maxY: modelBounds.bottom,
    });
    const bounds: ViewportRect = {
      left: offsetBounds.minX,
      top: offsetBounds.minY,
      right: offsetBounds.maxX,
      bottom: offsetBounds.maxY,
    };
    const candidateIds = this.spatial.intersect(bounds);

    // Filter to model-space ids only (#2 — layout-space entities must not
    // leak into VP visible sets; the spatial index may contain entities from
    // multiple spaces when in non-streaming layout mode).
    // Also filter out entities on layers frozen in this viewport.
    const frozenSet = new Set(viewport.frozenLayers);
    const visibleIds = new Set<string>();

    for (const id of candidateIds) {
      const entity = this.spatial.resolve(id);
      // Restrict to model-space; conservatively include unresolved ids so
      // transient remove-races don't blank the viewport entirely.
      if (entity && entity.spaceId !== "model") continue;
      // Skip frozen layers.
      if (entity && frozenSet.size > 0 && frozenSet.has(entity.layer)) continue;
      visibleIds.add(id);
    }

    const entry: CacheEntry = {
      visibleIds,
      viewCenterX: viewport.viewCenter.x,
      viewCenterY: viewport.viewCenter.y,
      viewTargetX: viewport.viewTarget?.x ?? 0,
      viewTargetY: viewport.viewTarget?.y ?? 0,
      viewHeight: viewport.viewHeight,
      viewTwistAngle: viewport.viewTwistAngle,
      vpWidth: viewport.width,
      vpHeight: viewport.height,
      frozenLayersKey: frozenKey,
    };
    this.cache.set(viewport.id, entry);
    return visibleIds;
  }

  /**
   * Invalidate cached results.  Pass a viewport id to evict a single entry;
   * omit the argument to clear all entries (e.g. on layer change or import).
   */
  invalidate(viewportId?: string): void {
    if (viewportId !== undefined) {
      this.cache.delete(viewportId);
    } else {
      this.cache.clear();
    }
  }
}
